import React from "react";
import { formatCurrency } from "../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Plus, Trash2 } from "lucide-react";

// One responsive row shared by quotes and invoices: fields sit in a horizontal
// row on >=sm and stack full-width with their own labels on phones, where the
// old fixed-width flex row overflowed.
export function LineItemRow({ item, index, onUpdate, onRemove, canRemove }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3 py-3">
      <div className="flex-1 space-y-1">
        <Label className="sm:hidden text-xs">Description</Label>
        <Input
          placeholder="Description"
          value={item.description}
          onChange={(e) => onUpdate(index, "description", e.target.value)}
          className="w-full"
        />
      </div>
      <div className="flex gap-2 sm:gap-3">
        <div className="flex-1 sm:w-20 sm:flex-none space-y-1">
          <Label className="sm:hidden text-xs">Qty</Label>
          <Input
            type="number"
            placeholder="Qty"
            value={item.quantity}
            onChange={(e) => onUpdate(index, "quantity", parseInt(e.target.value) || 0)}
            className="w-full"
          />
        </div>
        <div className="flex-1 sm:w-28 sm:flex-none space-y-1">
          <Label className="sm:hidden text-xs">Price</Label>
          <Input
            type="number"
            placeholder="Price"
            value={item.price}
            onChange={(e) => onUpdate(index, "price", parseFloat(e.target.value) || 0)}
            className="w-full"
          />
        </div>
      </div>
      <div className="flex items-center justify-between sm:justify-end gap-2 sm:pt-2">
        <span className="sm:hidden text-xs text-muted-foreground">Line total</span>
        <span className="sm:w-24 text-right font-mono">{formatCurrency(item.quantity * item.price)}</span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onRemove(index)}
          disabled={!canRemove}
          aria-label="Remove item"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

// The full "Line Items" card — rows + running subtotal/tax/total, computed here
// so the four create/edit pages don't each repeat the math.
export function LineItemsSection({ items, onAddItem, onUpdateItem, onRemoveItem, taxRate = 0.1 }) {
  let subtotal = 0;
  for (let i = 0; i < items.length; i++) {
    subtotal += items[i].quantity * items[i].price;
  }
  const tax = subtotal * taxRate;
  const total = subtotal + tax;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Line Items</CardTitle>
        <Button type="button" variant="outline" size="sm" onClick={onAddItem}>
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </CardHeader>
      <CardContent>
        <div className="divide-y">
          {items.map((it, i) => (
            <LineItemRow
              key={i}
              item={it}
              index={i}
              onUpdate={onUpdateItem}
              onRemove={onRemoveItem}
              canRemove={items.length > 1}
            />
          ))}
        </div>
        <div className="mt-6 pt-6 border-t space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Subtotal</span>
            <span className="font-mono">{formatCurrency(subtotal)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Tax ({Math.round(taxRate * 100)}%)</span>
            <span className="font-mono">{formatCurrency(tax)}</span>
          </div>
          <div className="flex justify-between text-lg font-bold pt-2">
            <span>Total</span>
            <span className="font-mono text-primary">{formatCurrency(total)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
